import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { AddressAutocomplete, type AddressDetails } from '@/components/ui/address-autocomplete'
import { useToast } from '@/hooks/use-toast'
import { useCreateContact, useUsers, useAccounts } from '@/hooks/use-api'
import { useDebounce } from '@/hooks/use-debounce'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { cn } from '@/lib/utils'
import { DuplicateWarning } from '@/components/DuplicateWarning'
import { useUser } from '@/contexts/UserContext'
import { ownerPickerOptions } from '@/lib/salesReps'
import type { Contact } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateContactModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId?: string
  ownerId?: string
  onCreated?: (contact: Contact) => void
}

export function CreateContactModal({
  open,
  onOpenChange,
  accountId,
  ownerId,
  onCreated,
}: CreateContactModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateContact()
  const { data: usersData } = useUsers({ limit: 10000 }) // Load all users for dropdown

  const [accountSearch, setAccountSearch] = useState('')
  const debouncedAccountSearch = useDebounce(accountSearch, 300)
  const { data: accountsData, isFetching: accountsLoading } = useAccounts({
    search: debouncedAccountSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })

  const contactLeadSources = usePicklistOptions('contact', 'lead_source')

  const [formData, setFormData] = useState<Partial<Contact>>({
    first_name: '',
    last_name: '',
    email: '',
    secondary_email__c: '',
    phone: '',
    mobile: '',
    title: '',
    department: '',
    account_id: '',
    owner_id: '',
    lead_source: '',
    mailing_street: '',
    mailing_city: '',
    mailing_state: '',
    mailing_postal_code: '',
    mailing_country: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormData((prev) => ({
        ...prev,
        account_id: accountId || prev.account_id || '',
        owner_id: ownerId || currentUser?.id || prev.owner_id || '',
      }))
    }
  }, [open, accountId, ownerId, currentUser?.id])

  const users = usersData?.data || []
  const accounts = accountsData?.data || []

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.last_name?.trim()) {
      newErrors.last_name = 'Last name is required'
    }

    // A contact must have at least one number (phone OR mobile) — required on create.
    if (!formData.phone?.trim() && !formData.mobile?.trim()) {
      newErrors.phone = 'Enter a phone or mobile number'
    }

    if (formData.email && formData.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Email must be a valid email address'
      }
    }

    if (formData.secondary_email__c && formData.secondary_email__c.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.secondary_email__c)) {
        newErrors.secondary_email__c = 'Secondary email must be a valid email address'
      }
    }

    if (formData.phone && formData.phone.trim().length > 50) {
      newErrors.phone = 'Phone number must be 50 characters or less'
    }

    if (formData.mobile && formData.mobile.trim().length > 50) {
      newErrors.mobile = 'Mobile phone must be 50 characters or less'
    }

    if (
      formData.lead_source &&
      contactLeadSources.length > 0 &&
      !contactLeadSources.includes(formData.lead_source)
    ) {
      newErrors.lead_source = `Invalid lead source. Must be one of: ${contactLeadSources.join(', ')}`
    }

    if (
      formData.linkedin__c &&
      formData.linkedin__c.trim().length > 0 &&
      !/^https?:\/\/.+/.test(formData.linkedin__c)
    ) {
      newErrors.linkedin__c = 'LinkedIn must be a valid URL starting with http:// or https://'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      // Map frontend field names to backend field names
      const contactData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        secondary_email__c: formData.secondary_email__c,
        phone: formData.phone,
        mobile_phone: formData.mobile, // Map mobile to mobile_phone
        title: formData.title,
        department: formData.department,
        account_id: formData.account_id,
        owner_id: formData.owner_id,
        lead_source: formData.lead_source,
        mailing_street: formData.mailing_street,
        mailing_city: formData.mailing_city,
        mailing_state: formData.mailing_state,
        mailing_postal_code: formData.mailing_postal_code,
        mailing_country: formData.mailing_country,
      }

      const result = await createMutation.mutateAsync(contactData)

      // Surface non-fatal side-effect warnings (e.g. a Dead/Nurture create whose
      // Mailchimp unsubscribe / BDR task did not complete).
      const warnings = (result as { warnings?: string[] })?.warnings
      if (warnings && warnings.length) {
        toast({
          title: 'Contact created with warnings',
          description: warnings.join(' '),
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Success',
          description: 'Contact created successfully.',
        })
      }

      if (onCreated && result?.id) {
        onCreated(result as Contact)
      }

      // Reset form and close modal (preserve prepopulated values)
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        secondary_email__c: '',
        phone: '',
        mobile: '',
        title: '',
        department: '',
        account_id: accountId || '',
        owner_id: ownerId || currentUser?.id || '',
        lead_source: '',
        mailing_street: '',
        mailing_city: '',
        mailing_state: '',
        mailing_postal_code: '',
        mailing_country: '',
      })
      setErrors({})
      onOpenChange(false)
    } catch (error) {
      const responseData = (error as any)?.response?.data
      const errorMessage = responseData?.error || explainError(error, 'Failed to create contact')
      const details = responseData?.details
      toast({
        title: 'Error',
        description: details?.length ? details.join(', ') : errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-contact-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Contact</DialogTitle>
          <DialogDescription id="create-contact-description">
            Fill in the details to create a new contact. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* First Name */}
            <div>
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder="Enter first name"
              />
            </div>

            {/* Last Name */}
            <div>
              <Label htmlFor="last_name" className="required">
                Last Name *
              </Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder="Enter last name"
                aria-required="true"
                aria-invalid={!!errors.last_name}
                aria-describedby={errors.last_name ? 'last_name-error' : undefined}
              />
              {errors.last_name && (
                <p id="last_name-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.last_name}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="email@example.com"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <p id="email-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Secondary Email */}
            <div>
              <Label htmlFor="secondary_email">Secondary Email</Label>
              <Input
                id="secondary_email"
                type="email"
                value={formData.secondary_email__c}
                onChange={(e) => handleChange('secondary_email__c', e.target.value)}
                placeholder="alternate@example.com"
                aria-invalid={!!errors.secondary_email__c}
                aria-describedby={errors.secondary_email__c ? 'secondary-email-error' : undefined}
              />
              {errors.secondary_email__c && (
                <p
                  id="secondary-email-error"
                  className="text-sm text-destructive mt-1"
                  role="alert"
                >
                  {errors.secondary_email__c}
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter job title"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Phone */}
            <div>
              <Label htmlFor="phone">
                Phone{' '}
                <span className="text-xs font-normal text-muted-foreground">
                  (phone or mobile required)
                </span>
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="Enter phone number"
                aria-invalid={!!errors.phone}
                aria-describedby={errors.phone ? 'phone-error' : undefined}
              />
              {errors.phone && (
                <p id="phone-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Mobile Phone */}
            <div>
              <Label htmlFor="mobile">Mobile Phone</Label>
              <Input
                id="mobile"
                value={formData.mobile}
                onChange={(e) => handleChange('mobile', e.target.value)}
                placeholder="Enter mobile phone number"
                aria-invalid={!!errors.mobile}
                aria-describedby={errors.mobile ? 'mobile-error' : undefined}
              />
              {errors.mobile && (
                <p id="mobile-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.mobile}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Account */}
            <div>
              <Label htmlFor="account_id">Account</Label>
              <RecordCombobox
                id="account_id"
                records={accounts}
                value={formData.account_id as string}
                onValueChange={(value) => handleChange('account_id', value)}
                placeholder="Select account..."
                searchPlaceholder="Search accounts..."
                emptyMessage="No accounts found."
                displayField={(acc) => acc.name || acc.account_name || ''}
                aria-label="Account"
                onSearchChange={setAccountSearch}
                isLoading={accountsLoading}
                totalCount={accountsData?.total}
              />
            </div>

            {/* Department */}
            <div>
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                value={formData.department}
                onChange={(e) => handleChange('department', e.target.value)}
                placeholder="Enter department"
              />
            </div>
          </div>

          {/* Lead Source */}
          <div>
            <Label htmlFor="lead_source">Lead Source</Label>
            <select
              id="lead_source"
              value={formData.lead_source}
              onChange={(e) => handleChange('lead_source', e.target.value)}
              className={cn(
                'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                errors.lead_source && 'border-destructive'
              )}
              aria-invalid={!!errors.lead_source}
              aria-describedby={errors.lead_source ? 'create-lead-source-error' : undefined}
            >
              <option value="">Select lead source...</option>
              {contactLeadSources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            {errors.lead_source && (
              <p
                id="create-lead-source-error"
                className="text-sm text-destructive mt-1"
                role="alert"
              >
                {errors.lead_source}
              </p>
            )}
          </div>

          {/* Owner */}
          <div>
            <Label htmlFor="owner_id">Owner</Label>
            <select
              id="owner_id"
              value={formData.owner_id}
              onChange={(e) => handleChange('owner_id', e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Select owner...</option>
              {ownerPickerOptions(users, formData.owner_id).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Mailing Address Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium bg-muted px-3 py-1.5 rounded-sm">Mailing Address</h3>

            {/* Street with Autocomplete */}
            <AddressAutocomplete
              id="mailing_address"
              label="Street"
              value={formData.mailing_street || ''}
              onAddressSelect={(address: AddressDetails) => {
                setFormData((prev) => ({
                  ...prev,
                  mailing_street: address.street,
                  mailing_city: address.city,
                  mailing_state: address.state,
                  mailing_postal_code: address.postal_code,
                  mailing_country: address.country,
                }))
              }}
              onStreetChange={(street) => handleChange('mailing_street', street)}
              placeholder="Start typing address..."
            />

            <div className="grid grid-cols-2 gap-4">
              {/* City */}
              <div>
                <Label htmlFor="mailing_city">City</Label>
                <Input
                  id="mailing_city"
                  value={formData.mailing_city}
                  onChange={(e) => handleChange('mailing_city', e.target.value)}
                  placeholder="Enter city"
                />
              </div>

              {/* State */}
              <div>
                <Label htmlFor="mailing_state">State</Label>
                <Input
                  id="mailing_state"
                  value={formData.mailing_state}
                  onChange={(e) => handleChange('mailing_state', e.target.value)}
                  placeholder="Enter state"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Postal Code */}
              <div>
                <Label htmlFor="mailing_postal_code">Postal Code</Label>
                <Input
                  id="mailing_postal_code"
                  value={formData.mailing_postal_code}
                  onChange={(e) => handleChange('mailing_postal_code', e.target.value)}
                  placeholder="Enter postal code"
                />
              </div>

              {/* Country */}
              <div>
                <Label htmlFor="mailing_country">Country</Label>
                <Input
                  id="mailing_country"
                  value={formData.mailing_country}
                  onChange={(e) => handleChange('mailing_country', e.target.value)}
                  placeholder="Enter country"
                />
              </div>
            </div>
          </div>

          {/* Duplicate Warning */}
          <DuplicateWarning entityType="Contact" formData={formData as Record<string, unknown>} />

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Contact'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
